import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Cat from "@/game/Cat";
import sfx from "@/game/sfx";
import { useI18n } from "@/i18n";
import MilosRuns from "@/game/minigames/MilosRuns";
import MilosFishCatch from "@/game/minigames/MilosFishCatch";
import MilosYarnChase from "@/game/minigames/MilosYarnChase";
import MilosNapTimer from "@/game/minigames/MilosNapTimer";
import MilosMice from "@/game/minigames/MilosMice";

const CAT_W = 96;
const CAT_H = 96;
const WALK_SPEED = 380;
const EDGE = 90;

const SECTION_QUOTES = {
  intro: "hi! i'm Milkoş 🐾",
  manifesto: "she reads everything twice.",
  works: "six internships. i counted.",
  pour: "brew me a tiny one too?",
  projects: "these are still cooking.",
  "grid-match": "match 'em! i'm rooting for you.",
  skills: "she also knows how to open cans.",
  contact: "purr-please write her a note ♡",
};

// Palette per section — bubble color adapts nicely to the room's vibe
const SECTION_STYLE = {
  intro:        { bg: "#ffd7ba", ink: "#4a2a3a", accent: "#ff5d8f" },
  manifesto:    { bg: "#ffc4d6", ink: "#4a2a3a", accent: "#ff5d8f" },
  works:        { bg: "#fae1dd", ink: "#4a2a3a", accent: "#e15a4b" },
  pour:         { bg: "#fec89a", ink: "#4a2a3a", accent: "#ff5d8f" },
  projects:     { bg: "#cddafd", ink: "#4a2a3a", accent: "#4a9dbb" },
  "grid-match": { bg: "#fdd2e1", ink: "#4a2a3a", accent: "#ff5d8f" },
  skills:       { bg: "#bee1e6", ink: "#4a2a3a", accent: "#4a9dbb" },
  contact:      { bg: "#f8edeb", ink: "#4a2a3a", accent: "#ff5d8f" },
  default:      { bg: "#fff1e6", ink: "#4a2a3a", accent: "#ff5d8f" },
};

const MEOWS = ["meow?", "mrrrp!", "purr…", "prrt", "hi hi hi", "☾", "*blink*"];

// Cat mood based on total games played (tracked across arcade sessions)
function computeMood(plays) {
  if (plays === 0) return "curious";
  if (plays >= 20) return "excited";
  if (plays >= 8) return "playful";
  if (plays >= 3) return "curious";
  return "curious";
}

export default function PersistentCat({ catRef: externalRef, openGameSignal }) {
  const { t } = useI18n();
  const [pos, setPos] = useState({
    x: 120,
    y: typeof window !== "undefined" ? window.innerHeight - CAT_H - 80 : 500,
  });
  const posRef = useRef(pos);
  const [dir, setDir] = useState(1);
  const [walking, setWalking] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [muted, setMuted] = useState(() => sfx.isMuted());

  const [bubble, setBubble] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [gameOpen, setGameOpen] = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ dx: 0, dy: 0 });
  const [section, setSection] = useState("default");
  const [plays, setPlays] = useState(() => {
    try {
      return parseInt(localStorage.getItem("milkos_plays") || "0", 10);
    } catch (e) {
      return 0;
    }
  });
  const lastInputRef = useRef(Date.now());
  const idleTargetRef = useRef(null);

  const mood = computeMood(plays);
  const bubbleStyle = SECTION_STYLE[section] || SECTION_STYLE.default;

  const keysRef = useRef({});
  const lastTsRef = useRef(0);
  const rafRef = useRef();

  const say = (text, ms = 3400, sound = null) => {
    setBubble({ text, id: Date.now() });
    if (sound && !muted) sfx[sound]();
    setTimeout(() => setBubble((b) => (b && Date.now() - b.id >= ms - 50 ? null : b)), ms);
  };

  // BUG FIX — openGameSignal from GameDoors → open the matching game
  useEffect(() => {
    if (openGameSignal && openGameSignal.id) {
      setMenuOpen(false);
      setGameOpen(openGameSignal.id);
      if (!muted) sfx.pop();
      setPlays((p) => {
        const n = p + 1;
        try {
          localStorage.setItem("milkos_plays", String(n));
        } catch (e) {
          /* noop */
        }
        return n;
      });
    }
  }, [openGameSignal]);

  // Hover-only bubble state
  const [hovering, setHovering] = useState(false);

  // Intro speech — only fires ONCE on first hover, not automatically
  const introShownRef = useRef(false);

  // Section observer — silent (no auto-bubble anymore, just track section for bubble color)
  useEffect(() => {
    const sectionQuoteKey = {
      intro: "cat.intro",
      manifesto: "sec.manifesto",
      works: "sec.works",
      pour: "sec.pour",
      projects: "sec.projects",
      "grid-match": "sec.grid",
      skills: "sec.skills",
      contact: "sec.contact",
    };
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio > 0.4) {
            setSection(e.target.id);
          }
        }
      },
      { threshold: [0.4] },
    );
    for (const id of Object.keys(sectionQuoteKey)) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [t]);

  // Resize
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const p = posRef.current;
      const nx = Math.max(0, Math.min(w - CAT_W, p.x));
      const ny = Math.max(0, Math.min(h - CAT_H, p.y));
      posRef.current = { x: nx, y: ny };
      setPos({ x: nx, y: ny });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keyboard
  useEffect(() => {
    const isFormEl = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || el.isContentEditable;
    };
    const isMinigameFocus = () =>
      !!document.querySelector('[data-minigame-active="true"]');
    const down = (e) => {
      if (isFormEl(document.activeElement)) return;
      if (isMinigameFocus() && (e.code === "Space" || e.code === "KeyE")) return;
      if (gameOpen) return;
      if (
        e.code === "ArrowLeft" || e.code === "ArrowRight" ||
        e.code === "ArrowUp" || e.code === "ArrowDown"
      ) {
        keysRef.current[e.code] = true;
        e.preventDefault();
      }
      if (e.code === "KeyH") setHidden((h) => !h);
      if (e.code === "KeyM") {
        const next = !muted;
        setMuted(next);
        sfx.setMuted(next);
      }
    };
    const up = (e) => (keysRef.current[e.code] = false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [gameOpen, muted]);

  // Drag
  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      const nx = Math.max(0, Math.min(window.innerWidth - CAT_W, cx - dragRef.current.dx));
      const ny = Math.max(0, Math.min(window.innerHeight - CAT_H, cy - dragRef.current.dy));
      posRef.current = { x: nx, y: ny };
      setPos({ x: nx, y: ny });
    };
    const up = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [dragging]);

  // Movement loop
  useEffect(() => {
    const loop = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;
      if (!dragging && !gameOpen) {
        const w = window.innerWidth;
        const h = window.innerHeight;
        const keys = keysRef.current;
        let dx = 0, dy = 0;
        if (keys["ArrowLeft"]) dx -= 1;
        if (keys["ArrowRight"]) dx += 1;
        if (keys["ArrowUp"]) dy -= 1;
        if (keys["ArrowDown"]) dy += 1;
        const hasInput = dx !== 0 || dy !== 0;
        if (hasInput) {
          lastInputRef.current = Date.now();
          idleTargetRef.current = null;
        }

        // Idle wander after 6s of no input
        const idleFor = Date.now() - lastInputRef.current;
        if (!hasInput && idleFor > 6000 && !menuOpen && !bubble) {
          if (!idleTargetRef.current) {
            const margin = 100;
            idleTargetRef.current = {
              x: margin + Math.random() * (w - CAT_W - margin * 2),
              y: h - CAT_H - 60 - Math.random() * 40,
              until: Date.now() + 1200 + Math.random() * 2000,
            };
          } else if (Date.now() > idleTargetRef.current.until) {
            idleTargetRef.current = null;
          } else {
            const target = idleTargetRef.current;
            const tdx = target.x - posRef.current.x;
            const tdy = target.y - posRef.current.y;
            const dist = Math.hypot(tdx, tdy);
            if (dist > 12) {
              // Deadzone: only set dx/dy when significantly off — avoids jitter
              dx = Math.abs(tdx) > 8 ? Math.sign(tdx) : 0;
              dy = Math.abs(tdy) > 8 ? Math.sign(tdy) : 0;
            } else {
              idleTargetRef.current = null;
            }
          }
        }

        const p = posRef.current;
        const speed = hasInput ? WALK_SPEED : WALK_SPEED * 0.55;
        let nx = Math.max(0, Math.min(w - CAT_W, p.x + dx * speed * dt));
        let ny = Math.max(0, Math.min(h - CAT_H, p.y + dy * speed * dt));
        if (dx > 0) setDir(1);
        else if (dx < 0) setDir(-1);
        // else keep last direction — prevents flip flicker
        setWalking(dx !== 0 || dy !== 0);
        const nearBottom = ny > h - CAT_H - EDGE;
        const nearTop = ny < EDGE;
        const step = 8 + WALK_SPEED * dt * 0.4;
        if (keys["ArrowDown"] && nearBottom &&
            window.scrollY < document.body.scrollHeight - h - 2)
          window.scrollBy({ top: step });
        if (keys["ArrowUp"] && nearTop && window.scrollY > 2)
          window.scrollBy({ top: -step });
        posRef.current = { x: nx, y: ny };
        if (externalRef) {
          externalRef.current = { x: nx, y: ny, w: CAT_W, h: CAT_H };
        }
        setPos({ x: nx, y: ny });
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      lastTsRef.current = 0;
      cancelAnimationFrame(rafRef.current);
    };
  }, [dragging, gameOpen]);

  const onCatMouseDown = (e) => {
    e.preventDefault();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { dx: cx - posRef.current.x, dy: cy - posRef.current.y };
    setDragging(true);
  };

  const onCatClick = () => {
    const moodMeows = {
      excited: ["MEOW!", "prrrt!", "hi hi hi!"],
      playful: ["mrrp!", "☾", "*head bonk*"],
      curious: MEOWS,
      sleepy: ["mrr…", "*yawn*", "zzz"],
    };
    const arr = moodMeows[mood] || MEOWS;
    const meow = arr[Math.floor(Math.random() * arr.length)];
    say(meow, 1800, "meow");
    setMenuOpen((v) => !v);
  };

  const openGame = (id) => {
    setMenuOpen(false);
    setGameOpen(id);
    if (!muted) sfx.pop();
    const next = plays + 1;
    setPlays(next);
    try {
      localStorage.setItem("milkos_plays", String(next));
    } catch (e) {
      /* noop */
    }
  };

  if (hidden) {
    return (
      <button
        onClick={() => setHidden(false)}
        className="fixed bottom-6 left-6 z-[80] font-mono-label text-[10px] uppercase tracking-widest border-2 border-[color:var(--ink)] rounded-full px-3 py-2 bg-[color:var(--paper)] shadow-[3px_3px_0_0_var(--ink)]"
        data-testid="cat-unhide"
      >
        {t("hint.unhide")}
      </button>
    );
  }

  return (
    <>
      {/* Cat sprite */}
      <div
        style={{
          position: "fixed",
          left: pos.x,
          top: pos.y,
          width: CAT_W,
          height: CAT_H,
          zIndex: 80,
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
        }}
        className={walking ? "cat-walking" : "cat-idle"}
        data-testid="persistent-cat"
        onMouseDown={onCatMouseDown}
        onTouchStart={onCatMouseDown}
        onClick={onCatClick}
        onMouseEnter={() => {
          setHovering(true);
          if (!introShownRef.current) {
            introShownRef.current = true;
            say(t("cat.intro"), 3200, "meow");
          } else {
            // subtle greeting on subsequent hovers
            const q = section && section !== "default" ? t(`sec.${
              section === "grid-match" ? "grid" : section === "intro" ? "manifesto" : section
            }`) : t("cat.intro");
            say(q, 2600);
          }
        }}
        onMouseLeave={() => setHovering(false)}
      >
        <Cat dir={dir} sitting={!walking} size={CAT_W} mood={mood} />
        <div
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono-label text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border-2 border-[color:var(--ink)] shadow-[2px_2px_0_0_var(--ink)]"
          style={{ background: bubbleStyle.accent, color: "#fff" }}
        >
          Milkoş
        </div>
      </div>

      {/* Speech bubble — big, readable, context-aware */}
      <AnimatePresence>
        {bubble && (
          <motion.div
            key={bubble.id}
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.9 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: "fixed",
              left: Math.min(window.innerWidth - 320, pos.x + CAT_W + 14),
              top: Math.max(14, pos.y - 40),
              zIndex: 81,
              background: bubbleStyle.bg,
              color: bubbleStyle.ink,
              borderColor: bubbleStyle.ink,
            }}
            className="max-w-[300px] px-5 py-4 rounded-3xl border-2 shadow-[4px_4px_0_0_var(--ink)]"
            data-testid="cat-bubble"
            data-cat-bubble-text={bubble.text}
          >
            <div
              className="font-serif-editorial italic leading-tight"
              style={{ fontSize: 22, letterSpacing: "-0.005em" }}
            >
              {bubble.text}
            </div>
            {/* Tail (pointing to cat, to the left) */}
            <svg
              className="absolute -left-3 bottom-4 overflow-visible"
              width="18"
              height="24"
              aria-hidden
            >
              <path
                d={`M 18 4 L 0 12 L 18 20 Z`}
                fill={bubbleStyle.bg}
                stroke={bubbleStyle.ink}
                strokeWidth="2"
                strokeLinejoin="round"
              />
              {/* mask right border seam */}
              <line x1="18" y1="4" x2="18" y2="20" stroke={bubbleStyle.bg} strokeWidth="3" />
            </svg>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            style={{
              position: "fixed",
              left: Math.min(window.innerWidth - 280, pos.x + CAT_W + 12),
              top: Math.max(10, Math.min(window.innerHeight - 500, pos.y + CAT_H - 60)),
              zIndex: 82,
            }}
            className="panel p-3 w-[260px]"
            data-testid="cat-menu"
          >
            <div className="font-mono-label text-[10px] uppercase tracking-widest opacity-70 px-2 pb-2 flex items-center justify-between">
              <span>{t("menu.title")}</span>
              <button
                onClick={() => {
                  const next = !muted;
                  setMuted(next);
                  sfx.setMuted(next);
                  if (!next) sfx.pop();
                }}
                data-testid="cat-mute"
                aria-label="Toggle sound"
                className="border-2 border-[color:var(--ink)] rounded-full px-2 py-0.5 bg-[color:var(--paper)]"
              >
                {muted ? "🔇" : "🔊"}
              </button>
            </div>
            {[
              { id: "runs", nameKey: "menu.runs", hint: "↑ jump · dino" },
              { id: "fish", nameKey: "menu.fish", hint: "← → catch" },
              { id: "yarn", nameKey: "menu.yarn", hint: "🖱 chase" },
              { id: "nap", nameKey: "menu.nap", hint: "hold Space" },
              { id: "mice", nameKey: "menu.mice", hint: "🐭 15s tap" },
            ].map((g) => (
              <button
                key={g.id}
                className="w-full text-left px-3 py-2 rounded-md hover:bg-[color:var(--pink)] transition-colors flex items-center justify-between"
                onClick={() => openGame(g.id)}
                data-testid={`cat-menu-${g.id}`}
              >
                <span className="display-serif italic text-lg">{t(g.nameKey)}</span>
                <span className="font-mono-label text-[9px] opacity-70">
                  {g.hint}
                </span>
              </button>
            ))}
            <div className="h-px bg-[color:var(--hair)] my-2" />
            <button
              className="w-full text-left px-3 py-2 rounded-md hover:bg-[color:var(--pink)] transition-colors flex items-center justify-between"
              onClick={() => {
                setMenuOpen(false);
                say("boop!", 1400, "boop");
              }}
              data-testid="cat-menu-boop"
            >
              <span className="display-serif italic text-lg">{t("menu.boop")}</span>
              <span className="font-mono-label text-[9px] opacity-70">for luck</span>
            </button>
            <button
              className="w-full text-left px-3 py-2 rounded-md hover:bg-[color:var(--pink)] transition-colors flex items-center justify-between"
              onClick={() => {
                setMenuOpen(false);
                setHidden(true);
              }}
              data-testid="cat-menu-hide"
            >
              <span className="display-serif italic text-lg">{t("menu.hide")}</span>
              <span className="font-mono-label text-[9px] opacity-70">shh</span>
            </button>
            <div className="px-3 pt-2 font-mono-label text-[9px] uppercase tracking-widest opacity-60">
              {t("menu.mood")} · {mood} · {t("menu.plays")} {plays}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cat game modal */}
      <AnimatePresence>
        {gameOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[85] bg-[rgba(74,42,58,0.5)] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="panel p-6 md:p-8 max-w-[720px] w-full max-h-[92vh] overflow-y-auto no-scrollbar relative"
              data-lenis-prevent
            >
              <div className="sticky -top-6 md:-top-8 z-20 flex justify-end pb-2 -mx-6 md:-mx-8 px-6 md:px-8 pt-1 bg-[color:var(--paper)]">
                <button
                  onClick={() => {
                    setGameOpen(null);
                    if (!muted) sfx.pop();
                  }}
                  className="font-mono-label text-[10px] uppercase tracking-widest border-2 border-[color:var(--ink)] rounded-full px-3 py-2 bg-[color:var(--paper)]"
                  data-testid="cat-game-close"
                >
                  Kapat ✕
                </button>
              </div>
              {gameOpen === "runs" && <MilosRuns onExit={() => setGameOpen(null)} />}
              {gameOpen === "fish" && (
                <MilosFishCatch onExit={() => setGameOpen(null)} />
              )}
              {gameOpen === "yarn" && (
                <MilosYarnChase onExit={() => setGameOpen(null)} />
              )}
              {gameOpen === "nap" && (
                <MilosNapTimer onExit={() => setGameOpen(null)} />
              )}
              {gameOpen === "mice" && (
                <MilosMice onExit={() => setGameOpen(null)} />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Controls hint */}
      <div
        className="fixed bottom-4 left-4 z-[81] flex items-center gap-2 bg-[color:var(--paper)] rounded-full border-2 border-[color:var(--ink)] px-3 py-1.5 shadow-[2px_2px_0_0_var(--ink)] font-mono-label text-[10px] uppercase tracking-widest"
        data-testid="cat-hint"
      >
        <span className="kbd">←</span>
        <span className="kbd">→</span>
        <span className="kbd">↑</span>
        <span className="kbd">↓</span>
        <span className="opacity-70">{t("hint.walk")}</span>
        <span className="opacity-40">·</span>
        <span className="opacity-70">{t("hint.click")}</span>
        <span className="opacity-40">·</span>
        <span className="kbd">M</span>
        <span className="opacity-70">{muted ? t("hint.unmute") : t("hint.mute")}</span>
        <span className="opacity-40">·</span>
        <button
          onClick={() => setHidden(true)}
          className="opacity-70 hover:opacity-100 underline"
          data-testid="cat-hide"
        >
          {t("hint.hide")}
        </button>
      </div>
    </>
  );
}
